import { EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { GaliaArea } from '@features/components/visualization/mapbox/models/galia-area';
import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';

export type MapEventType =
  | 'update-markers'
  | 'update-routes'
  | 'update-areas'
  | 'map-move'
  | 'map-zoom';

export type MapDataType = GaliaMarker[] | GaliaRoute[] | GaliaArea[];

export class MapEvents {
  static readonly eventEmitter = new EventEmitter<MapDataType>();
  private static instance: MapEvents;
  private readonly mapEvents: Map<string, EventEmitter<MapDataType>> = new Map();
  private readonly debouncedEvents: Map<string, NodeJS.Timeout> = new Map();
  private readonly throttledEvents: Map<string, number> = new Map();

  // Performance configuration
  private readonly DEBOUNCE_DELAYS = {
    'update-markers': 200,
    'update-routes': 200,
    'update-areas': 200,
    'map-move': 100,
    'map-zoom': 150
  };

  private readonly THROTTLE_DELAYS = {
    'map-move': 16, // ~60fps
    'map-zoom': 16,
    'update-markers': undefined,
    'update-routes': undefined,
    'update-areas': undefined
  };

  constructor() {}

  static getInstance(): MapEvents {
    if (!MapEvents.instance) {
      MapEvents.instance = new MapEvents();
    }
    return MapEvents.instance;
  }

  static reset(): void {
    if (MapEvents.instance) {
      MapEvents.instance.cleanup();
      MapEvents.instance = undefined!;
    }
  }

  cleanup(): void {
    this.debouncedEvents.forEach((timeout) => clearTimeout(timeout));
    this.debouncedEvents.clear();
    this.throttledEvents.clear();

    this.mapEvents.forEach((emitter) => emitter.complete());
    this.mapEvents.clear();
  }

  emit(event: MapEventType, data: MapDataType): void {
    if (this.shouldThrottle(event)) {
      return;
    }

    if (this.shouldDebounce(event)) {
      this.debouncedEmit(event, data);
    } else {
      this.immediateEmit(event, data);
    }
  }

  private shouldThrottle(event: MapEventType): boolean {
    const throttleDelay = this.THROTTLE_DELAYS[event];
    if (!throttleDelay) return false;

    const lastEmit = this.throttledEvents.get(event) || 0;
    const now = Date.now();

    if (now - lastEmit < throttleDelay) {
      return true;
    }

    this.throttledEvents.set(event, now);
    return false;
  }

  private shouldDebounce(event: MapEventType): boolean {
    return (
      event === 'update-markers' || event === 'update-routes' || event === 'update-areas'
    );
  }

  private debouncedEmit(event: MapEventType, data: MapDataType): void {
    const existingTimeout = this.debouncedEvents.get(event);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const delay = this.DEBOUNCE_DELAYS[event] || 200;
    const timeout = setTimeout(() => {
      this.immediateEmit(event, data);
      this.debouncedEvents.delete(event);
    }, delay);

    this.debouncedEvents.set(event, timeout);
  }

  private immediateEmit(event: MapEventType, data: MapDataType): void {
    if (this.mapEvents.has(event)) {
      this.mapEvents.get(event)!.emit(data);
    }
  }

  getInstance(): EventEmitter<MapDataType> {
    return MapEvents.eventEmitter;
  }

  on<T>(event: MapEventType, callback: (data: T) => void): Subscription {
    if (!this.mapEvents.has(event)) {
      this.mapEvents.set(event, new EventEmitter());
    }

    const eventEmitter = this.mapEvents.get(event)!;

    return eventEmitter
      .pipe(
        debounceTime(50),
        distinctUntilChanged((prev, curr) => {
          if (Array.isArray(prev) && Array.isArray(curr)) {
            // First check length for fast rejection
            if (prev.length !== curr.length) {
              return false;
            }
            // Compare actual content by stringifying
            try {
              return JSON.stringify(prev) === JSON.stringify(curr);
            } catch {
              // Fallback to reference equality if stringify fails
              return prev === curr;
            }
          }
          return prev === curr;
        })
      )
      .subscribe(callback as (event: MapDataType) => void);
  }
}
